import moment from 'moment';

import { DataGridColSettings } from '@/components/DataGrid/utils';
import { USStates } from '@/constants';
import { genderInput } from '@/constants/form_inputs';
import { LicensePlateTypeCodes } from '@/constants/licenceType';

export interface Person {
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth: Date | null;
  sex: string;
  state: string;
  operatorLicenseNumber: string;
  imageIndicator: boolean;
}

export interface Vehicle {
  licensePlateNumber: string;
  licensePlateYear: number | null;
  licensePlateTypeCode: string;
  vin: string;
  vehicleMakeCode: string;
  vehicleYear: number | null;
}

export interface FullNCICRecord extends Person, Vehicle {}

export const NCICDefaultFormValue: FullNCICRecord = {
  firstName: '',
  middleName: undefined,
  lastName: '',
  dateOfBirth: null,
  sex: '',
  state: '',
  operatorLicenseNumber: '',
  imageIndicator: true,
  licensePlateNumber: '',
  licensePlateYear: null,
  licensePlateTypeCode: '',
  vin: '',
  vehicleMakeCode: '',
  vehicleYear: null,
};

export const NCICFormFields = [
  {
    title: '',
    name: '',
    items: [
      {
        name: 'lastName',
        type: 'text',
        label: 'Last Name',
        clearable: true,
        rules: {
          minLength: {
            value: 3,
            message: 'Name should be more than 3 characters.',
          },
          maxLength: {
            value: 200,
            message: 'Name should be less than 200 characters.',
          },
          pattern: {
            value: /^[A-Za-z\s.'-]*$/,
            message: 'Invalid name',
          },
        },
      },
      {
        name: 'firstName',
        type: 'text',
        label: 'First Name',
        clearable: true,
        rules: {
          minLength: {
            value: 2,
            message: 'Name should be more than 2 characters.',
          },
          maxLength: {
            value: 200,
            message: 'Name should be less than 200 characters.',
          },
          pattern: {
            value: /^[A-Za-z\s.'-]*$/,
            message: 'Invalid name',
          },
        },
      },
      {
        name: 'middleName',
        type: 'text',
        label: 'Middle Name',
        clearable: true,
        rules: {
          maxLength: {
            value: 50,
            message: 'Middle Name should be less than 50 characters.',
          },
          pattern: {
            value: /^[A-Za-z\s.'-]*$/,
            message: 'Invalid name',
          },
        },
      },
      {
        name: 'dateOfBirth',
        type: 'date',
        label: 'Date of birth',
        minDate: moment('1900-01-01'),
        maxDate: moment(),
        clearable: true,
      },
      {
        ...genderInput,
        name: 'sex',
        type: 'select',
        rules: {},
        clearable: true,
      },
      {
        name: 'state',
        type: 'autocomplete',
        items: USStates.map((state) => ({
          label: `${state.value} - ${state.label}`,
          value: state.value,
        })),
        label: 'State',
        clearable: true,
      },
      {
        name: 'operatorLicenseNumber',
        type: 'text',
        clearable: true,
        label: 'Operator Licence Number',
      },
      {
        name: 'imageIndicator',
        type: 'checkbox',
        label: 'Image Indicator',
        clearable: true,
        grid: {
          md: 12,
        },
      },
    ],
  },
  {
    title: '',
    name: '',
    items: [
      {
        name: 'licensePlateNumber',
        type: 'text',
        label: 'Licence Plate Number',
        clearable: true,
      },
      {
        name: 'vin',
        type: 'text',
        label: 'VIN',
        clearable: true,
      },
      {
        name: 'licensePlateYear',
        type: 'date',
        label: 'Licence Plate Year',
        openTo: 'year',
        displayFormat: 'YYYY',
        views: ['year'],
        clearable: true,
        grid: {
          md: 1.5,
        },
      },
      {
        name: 'licensePlateTypeCode',
        type: 'autocomplete',
        label: 'Licence Plate Type Code',
        items: LicensePlateTypeCodes.map((item) => ({
          label: `${item.value} - ${item.label}`,
          value: item.value,
        })),
        clearable: true,
        grid: {
          md: 1.5,
        },
      },
      {
        name: 'vehicleMakeCode',
        type: 'text',
        label: 'Vehicle Make Code',
        clearable: true,
        grid: {
          md: 1.5,
        },
      },
      {
        name: 'vehicleYear',
        type: 'date',
        label: 'Vehicle Year',
        clearable: true,
        grid: {
          md: 1.5,
        },
        openTo: 'year',
        displayFormat: 'YYYY',
        views: ['year'],
      },
    ],
  },
];

export const NCICViewFields = [
  {
    title: 'Search Criteria',
    size: { md: 12 },
    items: [
      {
        value: 'lastName',
        label: 'Last name',
      },
      {
        value: 'firstName',
        label: 'First name',
      },
      {
        value: 'middleName',
        label: 'Middle name',
      },
      {
        value: 'dateOfBirth',
        label: 'Date of birth',
      },
      {
        value: 'sex',
        label: 'Sex',
      },
      {
        value: 'state',
        label: 'State',
      },
      {
        value: 'operatorLicenseNumber',
        label: 'Operator Licence Number',
      },
      {
        value: 'imageIndicator',
        label: 'Image Indicator',
      },
      {
        value: 'licensePlateNumber',
        label: 'Licence Plate Number',
      },
      {
        value: 'vin',
        label: 'VIN',
      },
      {
        value: 'licensePlateYear',
        label: 'Licence Plate Year',
      },
      {
        value: 'licensePlateTypeCode',
        label: 'Licence Plate Type Code',
      },
      {
        value: 'vehicleMakeCode',
        label: 'Vehicle Make Code',
      },
      {
        value: 'vehicleYear',
        label: 'Vehicle Year',
      },
    ],
  },
];

export interface NCICListResponse {
  requestId: string; // ID
  uniqueId: string;
  account: string;
  agency: string;
  user: string;

  subject: string; // how to generate
  from: string; // agency or user?
  date: string;

  request: FullNCICRecord;
  response: Record<string, any>;
}

export interface NCICRequest {
  requestId: string;
  uniqueId: string;
  account: string;
  agency: string;
  user: string;
  request: Record<string, any>;
  response: Record<string, any>;
}

export const ncicSearchRequestsDataGridCols: DataGridColSettings[] = [
  {
    field: 'deviceId',
    headerName: 'Device ID',
    visible: true,
  },
  {
    field: 'deviceOri',
    headerName: 'Device ORI',
    width: 150,
    visible: true,
  },
  {
    field: 'mnemonic',
    headerName: 'Mnemonic',
    visible: true,
  },
  {
    field: 'lastName',
    headerName: 'Last name',
    width: 150,
    visible: true,
  },
  {
    field: 'firstName',
    headerName: 'First name',
    width: 150,
    visible: true,
  },
  {
    field: 'email',
    headerName: 'Email',
    width: 200,
    visible: true,
  },
  {
    field: 'badgeNumber',
    headerName: 'Badge number',
    width: 150,
    visible: true,
  },
  {
    field: 'department',
    headerName: 'Department',
    width: 180,
    visible: true,
  },
  {
    field: 'rank',
    headerName: 'Rank',
    width: 150,
    visible: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    visible: true,
  },
  {
    field: 'createdDate',
    headerName: 'Created date',
    width: 150,
    visible: true,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 100,
    visible: true,
  },
];

export interface NCICRequestRecord {
  uniqueId: string;
  requestId: string;
  account: string;
  agency: string;
  user: string;
  userStateId: string;
  request: Request;
  createdAt: number;
  response: Response[];
  updatedAt: number;
}

export interface Request {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  sex: string;
  state: string;
  operatorLicenseNumber: string;
  imageIndicator: boolean;
  licensePlateNumber: string;
  licensePlateYear: number | null;
  licensePlateTypeCode: string;
  vin: string;
  vehicleMakeCode: string;
  vehicleYear: number | null;
}

export interface Response {
  'api:Transaction': ApiTransaction;
}

export interface ApiTransaction {
  id: string;
  LawEnforcementTransaction: LawEnforcementTransaction;
}

export interface LawEnforcementTransaction {
  version: string;
  Session: Session;
  Transaction: Transaction;
}

export interface Session {
  Authentication: Authentication;
  Id: string;
  TotalSegments: string;
  SegmentRecombinationId: string;
  CurrentSegment: string;
}

export interface Authentication {
  Mnemonic: string;
  ORI: string;
}

export interface Transaction {
  Response: TransactionResponse;
}

export interface TransactionResponse {
  Request: Request2;
  Hit: Hit;
  Image: string;
  SecondaryRequests: string;
  ResponseType: string;
  MessageData: string;
  System: string;
  Class: string;
  MessageType: string;
  CurrentDataBlock: string;
}

export interface Request2 {
  Id: string;
}

export interface Hit {
  Details: string;
  Detected: string;
}
